import { ChevronDown } from 'lucide-react';
import { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { Dropdown } from '@aimsos/ui';
import { FILTER_BAR_BUTTON_CLASSNAME } from '../constants';
import DashboardButton from './DashboardButton';

/** One option. Its own memoised component, so the list hands every row a stable `onSelect`. */
const FilterOption = memo(({ value, label, onSelect }) => {
  const handleClick = useCallback(() => onSelect(value), [onSelect, value]);
  return <Dropdown.Item onClick={handleClick}>{label}</Dropdown.Item>;
});

FilterOption.displayName = 'FilterOption';

FilterOption.propTypes = {
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
};

/**
 * One filter of the dashboards filter bars (Created and Templates).
 *
 * **Portalled menu.** The bar sits in an `overflow-hidden` wrapper so it can
 * fold away on scroll. A menu rendered inside it made the browser scroll that
 * wrapper to reveal the focused item, and the wrapper stayed scrolled after
 * the menu closed — the whole bar slid out of view the moment a value was
 * picked (CT-5468). In a portal the menu is outside the wrapper, so nothing
 * inside it ever scrolls.
 *
 * **Never an empty menu.** A catalog that answers with no rows used to open
 * nothing at all, which reads as a broken control. It now opens one disabled
 * "No options available" row instead.
 */
const FilterDropdown = ({ label, options, onSelect, className }) => {
  const { t } = useTranslation('dashboards');

  return (
    <Dropdown
      usePortal
      menuClassName="scrollbar-aimsos-auto-sm"
      trigger={
        <DashboardButton
          variant="secondary"
          size="md"
          rightIcon={ChevronDown}
          className={[className, FILTER_BAR_BUTTON_CLASSNAME].filter(Boolean).join(' ')}
        >
          <span className="truncate">{label}</span>
        </DashboardButton>
      }
    >
      {options.length ? (
        options.map((option) => (
          <FilterOption key={option.value} value={option.value} label={option.label} onSelect={onSelect} />
        ))
      ) : (
        <Dropdown.Item disabled>{t('filters.noOptions')}</Dropdown.Item>
      )}
    </Dropdown>
  );
};

FilterDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  onSelect: PropTypes.func.isRequired,
  className: PropTypes.string,
};

export default memo(FilterDropdown);
